import { useState, useEffect } from 'react';
import AnalyticsQueries from '../../../../requests/queries/AnalyticsQueries';
import LoadingSpinner from '../../../common/Loading';

const Overview = ({ onNavigate }) => {
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState(null);

    useEffect(() => {
        loadOverviewStats();
    }, []);

    const loadOverviewStats = async () => {
        try {
            setIsLoading(true);
            const res = await AnalyticsQueries.getOverviewStats();
            setStats(res.data.stats);
            setIsLoading(false);
        } catch (err) {
            console.error('Erreur lors du chargement des statistiques Overview:', err);
            setError(err.response?.data?.message || 'Une erreur est survenue');
            setIsLoading(false);
        }
    };

    const handleActionClick = (section) => {
      onNavigate(section);
    };

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    const formatChange = (change) => {
        if (change === 0) return '0%';
        const sign = change >= 0 ? '+' : '';
        return `${sign}${change.toFixed(1)}%`;
    };

    return (
      <div className="overview-section">
        <div className="welcome-section">
          <h2>Bienvenue dans votre boutique</h2>
          <p className="welcome-subtitle">Gérez votre collection de vêtements streetwear</p>
        </div>
        
        <div className="stats-grid">
          <div className="stat-card featured">
            <div className="stat-icon">
              <i className="bi bi-tshirt"></i>
            </div>
            <div className="stat-content">
              <h3>Articles</h3>
              <p className="stat-number">{stats?.products?.total || 0}</p>
              <p className="stat-label">Vêtements en stock</p>
              {stats?.products && (
                <div className={`stat-trend ${stats.products.change >= 0 ? 'positive' : 'negative'}`}>
                  <i className={`bi bi-arrow-${stats.products.change >= 0 ? 'up' : 'down'}`}></i>
                  <span>{formatChange(stats.products.change)} ce mois</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <i className="bi bi-people"></i>
            </div>
            <div className="stat-content">
              <h3>Clients</h3>
              <p className="stat-number">{stats?.clients?.total || 0}</p>
              <p className="stat-label">Clients actifs</p>
              {stats?.clients && (
                <div className={`stat-trend ${stats.clients.change >= 0 ? 'positive' : 'negative'}`}>
                  <i className={`bi bi-arrow-${stats.clients.change >= 0 ? 'up' : 'down'}`}></i>
                  <span>{formatChange(stats.clients.change)} ce mois</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <i className="bi bi-cart-check"></i>
            </div>
            <div className="stat-content">
              <h3>Commandes</h3>
              <p className="stat-number">{stats?.orders?.total || 0}</p>
              <p className="stat-label">Ce mois</p>
              {stats?.orders && (
                <div className={`stat-trend ${stats.orders.change >= 0 ? 'positive' : 'negative'}`}>
                  <i className={`bi bi-arrow-${stats.orders.change >= 0 ? 'up' : 'down'}`}></i>
                  <span>{formatChange(stats.orders.change)} ce mois</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="stat-card">
            <div className="stat-icon">
              <i className="bi bi-currency-euro"></i>
            </div>
            <div className="stat-content">
              <h3>Revenus</h3>
              <p className="stat-number">
                €{stats?.revenue?.total?.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0,00'}
              </p>
              <p className="stat-label">Ce mois</p>
              {stats?.revenue && (
                <div className={`stat-trend ${stats.revenue.change >= 0 ? 'positive' : 'negative'}`}>
                  <i className={`bi bi-arrow-${stats.revenue.change >= 0 ? 'up' : 'down'}`}></i>
                  <span>{formatChange(stats.revenue.change)} ce mois</span>
                </div>
              )}
            </div>
          </div>
        </div>
  
        <div className="quick-actions">
          <h3>Actions rapides</h3>
          <div className="actions-grid">
            <div 
              className="action-card"
              onClick={() => handleActionClick('products')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleActionClick('products');
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Aller à la section Nouveau produit"
            >
              <i className="bi bi-plus-circle"></i>
              <h4>Nouveau produit</h4>
              <p>Add a garment to the collection</p>
            </div>
            <div 
              className="action-card"
              onClick={() => handleActionClick('clients')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleActionClick('clients');
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Aller à la section Clients"
            >
              <i className="bi bi-people"></i>
              <h4>Clients</h4>
              <p>Manage clients</p>
            </div>
            <div 
              className="action-card"
              onClick={() => handleActionClick('analytics')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleActionClick('analytics');
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Aller à la section Analytics"
            >
              <i className="bi bi-graph-up"></i>
              <h4>Analytics</h4>
              <p>See sales performance</p>
            </div>
            <div 
              className="action-card"
              onClick={() => handleActionClick('orders')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleActionClick('orders');
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Aller à la section Expéditions"
            >
              <i className="bi bi-truck"></i>
              <h4>Expéditions</h4>
              <p>Manage deliveries</p>
            </div>
            <div 
              className="action-card"
              onClick={() => handleActionClick('orders')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleActionClick('orders');
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Aller à la section Retours"
            >
              <i className="bi bi-arrow-clockwise"></i>
              <h4>Retours</h4>
              <p>Handle client returns</p>
            </div>
            <div 
              className="action-card"
              onClick={() => handleActionClick('secret-codes')}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleActionClick('secret-codes');
                }
              }}
              tabIndex={0}
              role="button"
              aria-label="Aller à la section Codes Secrets"
            >
              <i className="bi bi-key"></i>
              <h4>Codes Secrets</h4>
              <p>Manage secret access to the store</p>
            </div>
          </div>
        </div>
      </div>
    );
  };

export default Overview;